import { cn } from '@strivelabs/ui-core/lib/utils';
import type { AssignedUser } from '@/types/task';

/**
 * The colours an avatar falls back to when someone has no picture.
 *
 * Picked by name rather than by position, so the same person is the same
 * colour in the row, the panel and the filter menu.
 */
const AVATAR_COLOURS = [ '#5B7FB0', '#5E9B8A', '#9A7BA8', '#B08A57', '#C08497', '#6E8FA3' ];

const colourFor = (name: string) => {
  let hash = 0;

  for (let index = 0; index < name.length; index += 1) {
    hash = (hash * 31 + name.charCodeAt(index)) >>> 0;
  }

  return AVATAR_COLOURS[hash % AVATAR_COLOURS.length];
};

const initial = (name: string) => name.trim().charAt(0).toUpperCase() || '?';

interface TaskAvatarProps {
  person: Pick<AssignedUser, 'display_name' | 'image_url'>;
  /** Named on hover where the avatar stands alone, without the name beside it. */
  title?: string;
  className?: string;
}

/** Someone's picture, or their initial on a colour drawn from their name. */
export const TaskAvatar = ({ person, title, className }: TaskAvatarProps) => {
  const shared = cn(
    'inline-flex size-[22px] shrink-0 items-center justify-center rounded-full',
    'text-[9.5px] font-semibold text-white',
    className,
  );

  if (person.image_url) {
    return <img src={person.image_url} alt="" title={title} className={cn(shared, 'object-cover')} />;
  }

  return (
    <span
      className={shared}
      style={{ background: colourFor(person.display_name) }}
      title={title}
      aria-hidden={title ? undefined : true}
    >
      {initial(person.display_name)}
    </span>
  );
};

interface TaskPersonProps {
  person: Pick<AssignedUser, 'display_name' | 'image_url'> | null;
  /** Shown in place of a name when nobody holds the role. */
  empty?: string;
  /** The table's property-cell scale (artifact `--prop-avatar`): a 16px
   *  avatar on the shared 7px icon-to-text gap. Menus and the panel keep the
   *  larger default. */
  compact?: boolean;
}

/** A person's avatar and name, or an em dash when the role is empty. */
export const TaskPerson = ({ person, empty = '—', compact = false }: TaskPersonProps) => {
  if (!person) {
    return <span className="text-[#B7B4AC]">{empty}</span>;
  }

  return (
    <span className={cn('inline-flex min-w-0 items-center', compact ? 'gap-[7px]' : 'gap-2')}>
      <TaskAvatar person={person} className={compact ? 'size-4 text-[8px]' : undefined} />
      <span className="truncate text-[13.5px] text-[#696969]">{person.display_name}</span>
    </span>
  );
};
